// shared-lib/cabinet-runs/companyOffering.js
//
// The COMPANY layer of a cabinet family's offering (issue #2460; plan
// claude-docs/2026-10-09-contractor-default-products-plan.md, "narrow, don't
// add a second judge"). A family says what it SELLS (`familyOffering.js`);
// the company's price book says what IT offers of that, as `offered` flags
// (`../books-price-book/offering.js`). This filters the family's resolved
// offering to the company's, so everything that reads an offering — the
// misses (`lookNotOffered`), the pickers, the style tools — narrows with no
// code of its own.
//
// A door is offered by its own chain (door → line → default), or because a
// colour sold on it was ticked on its own: four Venus colours ticked means
// exactly those four, on the doors they are sold on. A colour is offered on a
// door by its chain (colour → door → line → default), or because a UNIT in it
// was ticked on its own (`cabinet-sku--<family>--<sku>--<code>`, one size in
// one colour) while the colour itself says nothing — the finer tick lifts the
// coarser, so B24 in Houston Frost ticked alone still lets a customer pick
// Houston Frost. A flag a level says itself wins at that level. No flags, or
// flags that remove nothing: the family's offering, unchanged.
//
// Pure. `company` is the `{ key: boolean }` offering map or the book's
// entries — the offering helpers read either.

const {
  offeringDefaults,
  offeredFlag,
  cabinetOffered,
  cabinetDoorKey,
  cabinetColourKey,
  finishCode,
} = require('../books-price-book/offering');

const saysNothing = (company) => !company || Object.keys(company).length === 0;

// A unit key's colour segment is the code made key-safe
// (`vendorCabinetPriceBookKey`): anything but letters, digits and '-' → '-'.
const keySafe = (code) => String(code).replace(/[^A-Za-z0-9-]/g, '-');

/** The key-safe colour codes of the family's units ticked offered. */
function unitTickedCodes(company, familyId) {
  const prefix = `cabinet-sku--${keySafe(familyId)}--`;
  const codes = new Set();
  for (const [key, value] of Object.entries(company)) {
    if (!key.startsWith(prefix) || offeredFlag(value) !== true) continue;
    codes.add(key.slice(key.lastIndexOf('--') + 2));
  }
  return codes;
}

/** Whether `design` sells finish `id` of `kind` (no list: every finish). */
const sellsOn = (design, kind, id) =>
  design.finishes
    ? (design.finishes[kind === 'paint' ? 'paint' : 'woods'] || []).includes(id)
    : true;

/**
 * `base` (a family offering, resolved) narrowed to what `company` offers of
 * it. Adds `narrowedBy: 'company'` and `familyNotOffered` when it narrows.
 */
function narrowToCompany(base, company) {
  if (!base.familyId || saysNothing(company)) return base;
  const familyId = base.familyId;
  const byDefault = offeringDefaults(company)();
  const said = (key) => offeredFlag(company[key]);
  const tickedUnits = unitTickedCodes(company, familyId);
  // a unit ticked alone lifts its colour, unless the colour says otherwise
  const liftedByUnit = (code) =>
    tickedUnits.has(keySafe(code)) &&
    said(cabinetColourKey(familyId, code)) === null;
  const chain = (question) =>
    cabinetOffered({ entries: company, familyId, byDefault, ...question })
      .offered;
  const codesOn = (design) => [
    ...base.paint
      .filter((p) => sellsOn(design, 'paint', p.id))
      .map((p) => finishCode(p)),
    ...(base.woods || []).filter((w) => sellsOn(design, 'wood', w)),
  ];
  const doorOffered = (design) => {
    const own = said(cabinetDoorKey(familyId, design.id));
    if (own !== null) return own;
    const tickedOnIt = codesOn(design).some(
      (code) =>
        said(cabinetColourKey(familyId, code)) === true || liftedByUnit(code)
    );
    return tickedOnIt || chain({ designId: design.id });
  };
  const doorDesigns = base.doorDesigns.filter(doorOffered);
  const offeredOnADoor = (kind, id, code) =>
    doorDesigns.some(
      (d) =>
        sellsOn(d, kind, id) &&
        (chain({ designId: d.id, colourCode: code }) || liftedByUnit(code))
    );
  const paint = base.paint.filter((p) =>
    offeredOnADoor('paint', p.id, finishCode(p))
  );
  const woods = base.woods
    ? base.woods.filter((w) => offeredOnADoor('wood', w, w))
    : null;
  const narrowed = {
    door: doorDesigns.length < base.doorDesigns.length,
    paint: paint.length < base.paint.length,
    wood: !!woods && woods.length < base.woods.length,
  };
  if (!narrowed.door && !narrowed.paint && !narrowed.wood) return base;
  return {
    ...base,
    explain: true,
    narrowedBy: 'company',
    familyNotOffered: doorDesigns.length === 0,
    restricts: {
      door: base.restricts.door || narrowed.door,
      paint: base.restricts.paint || narrowed.paint,
      wood: base.restricts.wood || narrowed.wood,
    },
    doorDesigns,
    paint,
    woods,
    defaults: narrowDefaults(base.defaults, { doorDesigns, paint, woods }),
  };
}

/**
 * The family's defaults where the company offers them: its default door, else
 * the first door offered; its default finish, else none — the first offered
 * finish is `defaultFinishOf`'s to pick, as for any family without one.
 */
function narrowDefaults(defaults, { doorDesigns, paint, woods }) {
  if (!defaults) return null;
  const next = { ...defaults };
  if (defaults.doorDesign !== undefined) {
    next.doorDesign = doorDesigns.some((d) => d.id === defaults.doorDesign)
      ? defaults.doorDesign
      : (doorDesigns[0]?.id ?? null);
  }
  const want = defaults.finish || null;
  const offered =
    !!want &&
    ((want.paint && paint.some((p) => p.id === want.paint)) ||
      (want.wood && !!woods && woods.includes(want.wood)));
  next.finish = offered ? want : null;
  return next;
}

module.exports = { narrowToCompany };
